import type { ReactElement, ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

import { cn } from '@shared/lib/utils';
import { Button } from './button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../overlay/dropdown-menu';

export interface SplitButtonAction {
  id: string;
  label: ReactNode;
  onSelect: () => void;
  disabled?: boolean;
}

export interface SplitButtonProps {
  primaryAction: SplitButtonAction;
  actions: readonly SplitButtonAction[];
  menuLabel: string;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
}

/** A fixed primary command with independently selectable secondary commands. */
export function SplitButton({
  primaryAction,
  actions,
  menuLabel,
  disabled = false,
  loading = false,
  className,
}: SplitButtonProps): ReactElement {
  const unavailable = disabled || loading;

  return (
    <div className={cn('inline-flex max-w-full items-stretch', className)}>
      <Button
        type="button"
        onClick={primaryAction.onSelect}
        disabled={unavailable || primaryAction.disabled}
        loading={loading}
        className="min-w-0 focus-visible:z-10"
      >
        {primaryAction.label}
      </Button>
      {actions.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              size="icon"
              aria-label={menuLabel}
              disabled={unavailable}
              className="shrink-0 border-l border-primary-foreground/30 focus-visible:z-10"
            >
              <ChevronDown aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="max-w-[var(--radix-dropdown-menu-content-available-width)]"
          >
            {actions.map((action) => (
              <DropdownMenuItem
                key={action.id}
                disabled={unavailable || action.disabled}
                onSelect={action.onSelect}
                className="min-h-11 break-words"
              >
                {action.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
}
